import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Flame, Star, ChevronRight, BarChart3, Trophy, Database, Archive } from 'lucide-react';
import HabitCard from './HabitCard';
import HabitAddWizard from './HabitAddWizard';
import HabitCheckInModal from './HabitCheckInModal';
import HabitDetail from './HabitDetail';
import HabitInsights from './HabitInsights';
import WeeklyReport from './WeeklyReport';
import RewardCelebrationModal from './RewardCelebrationModal';
import AutoMilestoneModal from './AutoMilestoneModal';
import {
  getAllHabits, saveHabit, deleteHabit as dbDeleteHabit,
  toggleHabitDone, savePerformanceLog, getLogsForHabit, getAllLogsForDate
} from '../../lib/habitDB';
import { seedDemoData } from '../../lib/seedDemoData';
import {
  getLocalDateStr, calculateStreak, calculateTotalXP, calculateLevel, getLevelName, xpForNextLevel,
  isHabitDueOnDate, countNewHabits, shouldSuggestProgression, checkAutoMilestone
} from '../../lib/habitUtils';

// Find which reward rule was JUST triggered — returns { rule, ruleIndex } or null
function findTriggeredReward(habit, logs) {
  const rules = habit.rewards?.bigRewardRules || [];
  const today = new Date();
  const todayStr = getLocalDateStr();
  for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
    const rule = rules[ruleIndex];
    if (rule.ruleType === 'streak') {
      const doneDates = new Set(logs.filter(l => l.done).map(l => l.date));
      let streak = 0;
      let checkDate = todayStr;
      while (doneDates.has(checkDate)) {
        streak++;
        const prev = new Date(checkDate + 'T12:00:00');
        prev.setDate(prev.getDate() - 1);
        checkDate = getLocalDateStr(prev);
      }
      if (streak === rule.threshold) return { rule, ruleIndex };
    }
    if (rule.ruleType === 'total_completions') {
      const count = logs.filter(l => l.done).length;
      if (count === rule.threshold) return { rule, ruleIndex };
    }
    if (rule.ruleType === 'weekly_rate') {
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      let done = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const y = d.getFullYear(), mo = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
        if (logs.some(l => l.date === `${y}-${mo}-${day}` && l.done)) done++;
      }
      if (done === rule.threshold) return { rule, ruleIndex };
    }
  }
  return null;
}

export default function HabitTracker({ onHabitsChanged }) {
  const [habits, setHabits] = useState([]);
  const [todayLogs, setTodayLogs] = useState({});
  const [allLogs, setAllLogs] = useState({});
  const [streaks, setStreaks] = useState({});
  const [showWizard, setShowWizard] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(null);
  const [detailHabit, setDetailHabit] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState(null);
  const [rewardModal, setRewardModal] = useState(null);
  const [milestoneModal, setMilestoneModal] = useState(null);
  const [levelUpAnim, setLevelUpAnim] = useState(false); // triggers XP bar animation
  const [progressionHint, setProgressionHint] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const prevLevelRef = useRef(null);

  const today = getLocalDateStr();

  // Load data
  const loadData = useCallback(async () => {
    try {
      const allHabits = await getAllHabits();
      setHabits(allHabits);

      const todayLogsList = await getAllLogsForDate(today);
      const logMap = {};
      todayLogsList.forEach(l => { logMap[l.habitId] = l; });
      setTodayLogs(logMap);

      // Load logs and streaks for active habits
      const logsMap = {};
      const streaksMap = {};
      const active = allHabits.filter(h => h.status !== 'archived');
      for (const h of active) {
        const logs = await getLogsForHabit(h.id);
        logsMap[h.id] = logs;
        streaksMap[h.id] = calculateStreak(h, logs);
      }
      setAllLogs(logsMap);
      setStreaks(streaksMap);

      // Notify parent about habit changes (for calendar reminder sync)
      if (onHabitsChanged) onHabitsChanged(allHabits, logsMap);
    } catch (err) {
      console.error('Error loading habit data:', err);
    } finally {
      setLoading(false);
    }
  }, [today, onHabitsChanged]);

  useEffect(() => { loadData(); }, [loadData]);

  // XP & Level
  const { totalXP, level, xpProgress } = useMemo(() => {
    const all = Object.values(allLogs).flat();
    const total = calculateTotalXP(all);
    return { totalXP: total, level: calculateLevel(total), xpProgress: xpForNextLevel(total) };
  }, [allLogs]);

  // Level-up detection: fires animation when level increases
  useEffect(() => {
    if (prevLevelRef.current !== null && level > prevLevelRef.current) {
      setLevelUpAnim(true);
      setTimeout(() => setLevelUpAnim(false), 3200);
    }
    prevLevelRef.current = level;
  }, [level]);

  // Today's habits
  const todayHabits = useMemo(() => {
    return habits
      .filter(h => isHabitDueOnDate(h, today))
      .sort((a, b) => {
        const aDone = todayLogs[a.id]?.done ? 1 : 0;
        const bDone = todayLogs[b.id]?.done ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        const statusOrder = { new: 0, maintenance: 1, paused: 2 };
        return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
      });
  }, [habits, todayLogs, today]);

  const todayDone = todayHabits.filter(h => todayLogs[h.id]?.done).length;
  const todayTotal = todayHabits.length;

  // Handlers
  const handleToggle = async (habit) => {
    const log = await toggleHabitDone(habit.id, today);
    if (log.done) {
      const updatedLogs = await getLogsForHabit(habit.id);
      const autoMilestone = checkAutoMilestone(habit, updatedLogs);
      const userReward = findTriggeredReward(habit, updatedLogs);
      if (autoMilestone?.type === 'habit-formed' || autoMilestone?.type === 'habit-anniversary') {
        setMilestoneModal({ habit, ...autoMilestone });
      } else if (userReward) {
        setRewardModal({ habit, rule: userReward.rule, ruleIndex: userReward.ruleIndex });
      } else if (autoMilestone) {
        setMilestoneModal({ habit, ...autoMilestone });
      } else {
        setCelebration(habit.title);
        setTimeout(() => setCelebration(null), 1500);
      }
    }
    await loadData();
  };

  const handlePerformanceSave = async (value, rating) => {
    if (!showCheckIn) return;
    await savePerformanceLog(showCheckIn.id, today, value, rating);
    const updatedLogs = await getLogsForHabit(showCheckIn.id);

    // 1. Goal reached? (performance habit reached its goalTarget)
    const goalTarget = showCheckIn.tracking?.goalTarget;
    if (goalTarget && Number(value) >= goalTarget) {
      setMilestoneModal({ habit: showCheckIn, type: 'goal-reached', value: Number(value) });
      setShowCheckIn(null);
      await loadData();
      return;
    }

    // 2. Auto milestones (habit-formed, streaks, etc.)
    const autoMilestone = checkAutoMilestone(showCheckIn, updatedLogs);
    const userReward = findTriggeredReward(showCheckIn, updatedLogs);
    if (autoMilestone?.type === 'habit-formed' || autoMilestone?.type === 'habit-anniversary') {
      setMilestoneModal({ habit: showCheckIn, ...autoMilestone });
    } else if (userReward) {
      setRewardModal({ habit: showCheckIn, rule: userReward.rule, ruleIndex: userReward.ruleIndex });
    } else if (autoMilestone) {
      setMilestoneModal({ habit: showCheckIn, ...autoMilestone });
    } else {
      setCelebration(showCheckIn.title);
      setTimeout(() => setCelebration(null), 1500);
    }
    if (shouldSuggestProgression(updatedLogs)) setProgressionHint(showCheckIn);
    setShowCheckIn(null);
    await loadData();
  };

  const handleGoalNewTarget = async (habit, newGoalTarget) => {
    await saveHabit({
      ...habit,
      tracking: { ...habit.tracking, goalTarget: newGoalTarget, currentTarget: newGoalTarget },
      updatedAt: new Date().toISOString()
    });
    setMilestoneModal(null);
    await loadData();
  };

  const handleSaveNextReward = async (habit, ruleIndex, newRule) => {
    const updatedRules = [...(habit.rewards?.bigRewardRules || [])];
    if (ruleIndex >= 0 && ruleIndex < updatedRules.length) {
      updatedRules[ruleIndex] = newRule;
    } else {
      updatedRules.push(newRule);
    }
    const updatedHabit = {
      ...habit,
      rewards: { ...habit.rewards, bigRewardRules: updatedRules },
      updatedAt: new Date().toISOString()
    };
    await saveHabit(updatedHabit);
    setRewardModal(null);
    await loadData();
  };

  // Option A: Zyklus endet → nur noch Stabilität, keine Streak-Rewards
  const handleMilestoneOptionA = async () => {
    const habit = milestoneModal?.habit;
    if (!habit) return;
    await saveHabit({
      ...habit,
      status: 'maintenance',
      bigMilestoneAchieved: true,
      updatedAt: new Date().toISOString()
    });
    setMilestoneModal(null);
    await loadData();
  };

  // Option B: Neue Meilensteine (180/365 Tage)
  const handleMilestoneOptionB = async () => {
    const habit = milestoneModal?.habit;
    if (!habit) return;
    await saveHabit({
      ...habit,
      bigMilestoneAchieved: true,
      bigMilestoneExtended: true,
      updatedAt: new Date().toISOString()
    });
    setMilestoneModal(null);
    await loadData();
  };

  // Close anniversary milestone and mark as achieved
  const handleMilestoneClose = async () => {
    const m = milestoneModal;
    if (m?.type === 'habit-anniversary' && m.habit) {
      const field = m.days >= 365 ? 'milestone365Achieved' : 'milestone180Achieved';
      await saveHabit({ ...m.habit, [field]: true, updatedAt: new Date().toISOString() });
      await loadData();
    }
    // goal-reached: just close, no state change needed (user chose not to set new goal)
    setMilestoneModal(null);
  };

  const handleCreateHabit = async (habit) => {
    await saveHabit(habit);
    setShowWizard(false);
    await loadData();
  };

  const handleUpdateHabit = async (habit) => {
    await saveHabit(habit);
    setDetailHabit(habit);
    await loadData();
  };

  const handleDeleteHabit = async (id) => {
    await dbDeleteHabit(id);
    setDetailHabit(null);
    await loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Detail view
  if (detailHabit) {
    return (
      <HabitDetail
        habit={detailHabit}
        logs={allLogs[detailHabit.id] || []}
        habits={habits}
        onBack={() => { setDetailHabit(null); loadData(); }}
        onUpdate={handleUpdateHabit}
        onDelete={handleDeleteHabit}
      />
    );
  }

  // Insights view
  if (showInsights) {
    return (
      <HabitInsights
        habits={habits}
        allLogs={allLogs}
        onBack={() => setShowInsights(false)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="habit-tracker">
      {/* Celebration toast */}
      {celebration && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-bounce">
          Nice. Du bist im Rhythmus.
        </div>
      )}

      {/* XP Bar */}
      <div className={`bg-white rounded-xl p-4 border transition-all duration-300 ${levelUpAnim ? 'border-amber-300 shadow-lg shadow-amber-100' : 'border-slate-100'}`}>
        {levelUpAnim && (
          <style>{`
            @keyframes xp-star-burst {
              0%   { transform: translateY(0) scale(0); opacity: 0; }
              30%  { opacity: 1; }
              100% { transform: translateY(-40px) scale(0.4); opacity: 0; }
            }
            @keyframes level-glow {
              0%,100% { text-shadow: 0 0 0px transparent; color: #1e293b; }
              40%     { text-shadow: 0 0 14px #f59e0b, 0 0 28px #f59e0b; color: #d97706; }
              70%     { text-shadow: 0 0 8px #f59e0b; color: #b45309; }
            }
            @keyframes badge-pop {
              0%   { transform: scale(1); }
              35%  { transform: scale(1.25); }
              60%  { transform: scale(0.95); }
              100% { transform: scale(1); }
            }
            @keyframes bar-pulse {
              0%,100% { box-shadow: 0 0 0 0 rgba(251,191,36,0); }
              50%     { box-shadow: 0 0 0 5px rgba(251,191,36,0.35); }
            }
            .level-glow-text { animation: level-glow 2.2s ease-in-out forwards; }
            .badge-pop       { animation: badge-pop 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards; }
            .bar-pulse-anim  { animation: bar-pulse 1.2s ease-in-out 3; }
          `}</style>
        )}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 relative">
            {/* Fireworks particles */}
            {levelUpAnim && [...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full pointer-events-none"
                style={{
                  backgroundColor: ['#f59e0b','#34d399','#60a5fa','#f472b6','#fb923c','#a78bfa','#fbbf24','#6ee7b7'][i],
                  left: `${6 + i * 9}px`,
                  top: '-4px',
                  animation: `xp-star-burst ${0.9 + i * 0.12}s ease-out ${i * 0.07}s forwards`
                }}
              />
            ))}
            <div className={`w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center ${levelUpAnim ? 'badge-pop' : ''}`}>
              <Star className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className={`text-sm font-bold ${levelUpAnim ? 'level-glow-text' : 'text-slate-800'}`}>
                {getLevelName(level)}
              </p>
              <p className="text-xs text-slate-400">Level {level} · {totalXP} XP</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {habits.length > 0 && (
              <>
                <button
                  onClick={() => setShowWeeklyReport(true)}
                  className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg"
                  title="Wochenreport"
                  data-testid="weekly-report-btn"
                >
                  <Trophy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowInsights(true)}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                  title="Insights"
                  data-testid="insights-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
              </>
            )}
            <div className="text-right">
              <p className="text-xs text-slate-500">{todayDone}/{todayTotal} heute</p>
              {countNewHabits(habits) > 0 && (
                <p className="text-[10px] text-emerald-600">{countNewHabits(habits)}/3 Aufbau</p>
              )}
            </div>
          </div>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${levelUpAnim ? 'bar-pulse-anim' : ''} bg-gradient-to-r from-amber-400 to-orange-500`}
            style={{ width: `${Math.min(100, xpProgress.progress * 100)}%` }}
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">{Math.round(xpProgress.progress * 100)}% bis Level {level + 1}</p>
      </div>

      {/* Progression Hint */}
      {progressionHint && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-800">Zeit zu steigern?</p>
            <p className="text-xs text-blue-600">"{progressionHint.title}" fällt dir leicht.</p>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => { setDetailHabit(progressionHint); setProgressionHint(null); }}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg"
            >
              Steigern
            </button>
            <button
              onClick={() => setProgressionHint(null)}
              className="px-2 py-1.5 text-blue-500 text-xs"
            >
              Später
            </button>
          </div>
        </div>
      )}

      {/* Today's Habits */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-slate-800">Heute</h2>
          {todayTotal > 0 && (
            <span className="text-xs text-slate-500">
              {todayDone === todayTotal ? 'Alles erledigt!' : `${todayDone} von ${todayTotal}`}
            </span>
          )}
        </div>

        {todayHabits.length === 0 ? (
          <div className="bg-white rounded-xl p-8 border border-slate-100 text-center space-y-3">
            <p className="text-slate-500 text-sm">Noch keine Habits für heute.</p>
            <button
              onClick={() => setShowWizard(true)}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 inline-flex items-center gap-1.5"
              data-testid="add-first-habit"
            >
              <Plus className="w-4 h-4" /> Erste Routine erstellen
            </button>
            <div>
              <button
                onClick={async () => {
                  setSeeding(true);
                  try {
                    await seedDemoData();
                    await loadData();
                  } finally {
                    setSeeding(false);
                  }
                }}
                disabled={seeding}
                className="px-3 py-1.5 text-slate-400 hover:text-blue-600 text-xs inline-flex items-center gap-1 transition-colors"
                data-testid="seed-demo-btn"
              >
                <Database className="w-3 h-3" />
                {seeding ? 'Wird geladen...' : 'Demo-Daten laden'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {todayHabits.map(habit => (
              <HabitCard
                key={habit.id}
                habit={habit}
                log={todayLogs[habit.id]}
                streak={streaks[habit.id] || 0}
                onToggle={handleToggle}
                onPerformanceClick={(h) => setShowCheckIn(h)}
                onDetailClick={(h) => setDetailHabit(h)}
              />
            ))}
          </div>
        )}
      </div>

      {/* All Habits Overview */}
      {habits.filter(h => h.status !== 'archived').length > todayHabits.length && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-2">Alle Habits</h3>
          <div className="space-y-1.5">
            {habits.filter(h => h.status !== 'archived' && !isHabitDueOnDate(h, today)).map(h => (
              <div
                key={h.id}
                onClick={() => setDetailHabit(h)}
                className="bg-white rounded-lg p-3 border border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm text-slate-600">{h.title}</p>
                  <span className={`text-xs ${h.status === 'paused' ? 'text-amber-500' : 'text-slate-400'}`}>
                    {h.status === 'paused' ? 'Pausiert' : h.schedule?.frequencyType === 'weekly' ? `${h.schedule.weeklyTargetCount}x/Woche` : ''}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Button + Demo + Archived */}
      {habits.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setShowWizard(true)}
            className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-sm font-medium text-slate-500 hover:border-emerald-300 hover:text-emerald-600 flex items-center justify-center gap-2 transition-colors"
            data-testid="add-habit-btn"
          >
            <Plus className="w-4 h-4" /> Routine hinzufügen
          </button>
          {habits.some(h => h.status === 'archived') && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="w-full py-1.5 text-slate-300 hover:text-slate-500 text-[11px] inline-flex items-center justify-center gap-1 transition-colors"
              data-testid="show-archived-btn"
            >
              <Archive className="w-3 h-3" />
              {showArchived ? 'Archivierte ausblenden' : `Archivierte anzeigen (${habits.filter(h => h.status === 'archived').length})`}
            </button>
          )}
          {showArchived && (
            <div className="space-y-1.5">
              {habits.filter(h => h.status === 'archived').map(h => (
                <div
                  key={h.id}
                  onClick={() => setDetailHabit(h)}
                  className="bg-slate-50 rounded-lg p-3 border border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-100 opacity-60"
                >
                  <div>
                    <p className="text-sm text-slate-500">{h.title}</p>
                    <span className="text-[10px] text-slate-400">Archiviert</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              ))}
            </div>
          )}
          <button
            onClick={async () => {
              setSeeding(true);
              try {
                await seedDemoData();
                await loadData();
              } finally {
                setSeeding(false);
              }
            }}
            disabled={seeding}
            className="w-full py-2 text-slate-300 hover:text-blue-500 text-[11px] inline-flex items-center justify-center gap-1 transition-colors"
            data-testid="seed-demo-btn-bottom"
          >
            <Database className="w-3 h-3" />
            {seeding ? 'Wird geladen...' : 'Demo-Daten laden (ersetzt aktuelle)'}
          </button>
        </div>
      )}

      {/* Modals */}
      {showWizard && (
        <HabitAddWizard habits={habits} onSave={handleCreateHabit} onClose={() => setShowWizard(false)} />
      )}
      {showCheckIn && (
        <HabitCheckInModal habit={showCheckIn} onSave={handlePerformanceSave} onClose={() => setShowCheckIn(null)} />
      )}
      {showWeeklyReport && (
        <WeeklyReport habits={habits} allLogs={allLogs} onClose={() => setShowWeeklyReport(false)} />
      )}

      {/* Reward Celebration Modal */}
      {rewardModal && (
        <RewardCelebrationModal
          habit={rewardModal.habit}
          triggeredRule={rewardModal.rule}
          ruleIndex={rewardModal.ruleIndex}
          onClose={() => setRewardModal(null)}
          onSaveNextReward={handleSaveNextReward}
        />
      )}

      {/* Auto Milestone Modal (Streak 7/30/60, Habit-Formed, Anniversary) */}
      {milestoneModal && (
        <AutoMilestoneModal
          milestoneType={milestoneModal.type}
          habit={milestoneModal.habit}
          daysSinceStart={milestoneModal.daysSinceStart}
          days={milestoneModal.days}
          value={milestoneModal.value}
          onClose={handleMilestoneClose}
          onOptionA={handleMilestoneOptionA}
          onOptionB={handleMilestoneOptionB}
          onSetNewGoal={handleGoalNewTarget}
        />
      )}
    </div>
  );
}
